import { useTranslation } from 'react-i18next';

import { EmptyState } from '../EmptyState/EmptyState';
import { Placeholder, placeholderTypes } from '../../Placeholder/Placeholder';
import { usePlaceholders } from '../../PageComponents/usePlaceholders';
import { getCategoryMainImage } from '../../../shared/helpers/categoryHelpers';
import { getImageSources } from '../../../helpers/images';
import { isSvpCategory } from '../../../modules/helpers';
import { ItemHorizontal } from '../FavouritesListHorizontal/ItemHorizontal/ItemHorizontal';
import { getAssetMainImage } from '../../../shared/helpers/assetHelpers';

import type {
  SvpCategoryAdditional,
  SvpCategoryId,
} from '@vgtv/api-client/lib/svp_category';
import type {
  SvpAssetAdditional,
  SvpAssetId,
} from '@vgtv/api-client/lib/svp_asset';
import type { FC } from 'react';

import * as styles from './FavouritesListVertical.module.scss';

interface FavouritesListVerticalProps {
  favourites: {
    item: SvpCategoryAdditional | SvpAssetAdditional;
    isFilled: boolean;
  }[];
  isLoading: boolean;
  handleClick: (
    id: SvpCategoryId | SvpAssetId,
    type: 'asset' | 'category'
  ) => void;
}

const FavouritesListVertical: FC<FavouritesListVerticalProps> = ({
  favourites,
  handleClick,
  isLoading,
}) => {
  const { t } = useTranslation();
  const placeholders = usePlaceholders(!isLoading, 3);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingHeader}></div>
        {placeholders.map((item, index) => (
          <Placeholder key={index} type={placeholderTypes.favouritesVertical} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>{t('My list')}</div>
      {favourites.length > 0 ? (
        favourites.map(({ item, isFilled }) => {
          const imageUrl = isSvpCategory(item)
            ? getCategoryMainImage(item)
            : getAssetMainImage(item);

          const imageSources = imageUrl
            ? getImageSources({
                imageUrl,
                imagePreset: 'categoriesList',
              })
            : undefined;

          return (
            <div key={item.id}>
              <ItemHorizontal
                handleIconClick={handleClick}
                isFilled={isFilled}
                item={item}
                imageSources={imageSources}
                title={item.title}
              />
            </div>
          );
        })
      ) : (
        <EmptyState />
      )}
    </div>
  );
};

export { FavouritesListVertical };
